import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Stage, Layer, Rect, Path, Group, Circle, Line, Text} from 'react-konva';
import { colorToArray, ensureBoolean, isColorBright, isNotEmpty, RGB_2_HEX } from '../../lib/ep.js';
import { getAvailableIntervals, getTeamInfoByID, getTeamName } from '../front/GuessScoreClient.js';

const real_board_size = { w:600, h:600 };	// px
const default_cache_props = { pixelRatio:2, imageSmoothingEnabled:true, offset:5, drawBorder:false };

const digit_box_w = 80;
const digit_w = digit_box_w;
const digit_h = 120;
const digit_box_h = 245;
const text_padding = 18;

const X2Icon = (props) => {
	const bg_props = {
		fill:`rgba(${props?.bg_color ?? '0, 0, 0'}, 1)`,
	};

	const contents_props = {
		stroke:`rgba(${props?.text_color ?? '255, 255, 255'}, 1)`,
		strokeWidth:4.5,
		lineCap:'round',
		lineJoin:'round',
		fill:'transparent',
	};

	return (
		<>
			<Path
				{...bg_props}
				data="M30-50V55c0,2.76-2.24,5-5,5h-50c-2.76,0-5-2.24-5-5V-50H30z"
			/>

			<Path
				{...contents_props}
				data="M3,22.67c0,0,0.21-7.67,8-7.67c5.45,0,7,5.06,7,7.67c0,3.81-2.74,5.81-7,8.51c-4.52,2.86-8,5.91-8,10.63c0,1.25,0,1.7,0,3.19h15"
			/>

			<Line
				{...contents_props}
				points={[ -20,45, -5,30 ]}
			/>

			<Line
				{...contents_props}
				points={[ -5,45, -20,30 ]}
			/>
		</>
	)
};

const X2 = forwardRef(({ w, h, ...props }, ref) => {
	const bg_color = props?.bg_color ?? '0, 0, 0';
	const text_color = props?.text_color ?? '255, 255, 255';

	const refs = useRef({});

	const outer_border_length = 2 * w + 2 * h;

	const fast_transition_props = {
		duration:1,
		easing:Konva.Easings.StrongEaseInOut,
	};

	const generic_transition_props = {
		duration:1.5,
		easing:Konva.Easings.StrongEaseInOut,
	};

	useImperativeHandle(ref, () => ({
		display() {
			refs.current['outer_rect'].to({
				dash:[outer_border_length, outer_border_length],
				...generic_transition_props,
			});

			setTimeout(() => {
				refs.current['icon'].to({
					y:0.5 * text_padding,
					...fast_transition_props,
				});
			}, (0.1 * generic_transition_props.duration * 1000));
		},

		hide() {
			refs.current['outer_rect'].to({
				dash:[0, outer_border_length],
				...generic_transition_props,
			});

			refs.current['icon'].to({
				y:-(60 + text_padding),
				...fast_transition_props,
			});
		},
	}), []);

	return (
		<>
			<Group
				ref={(node) => { refs.current['icon'] = node; }}
				x={w / 2}
				y={-(60 + text_padding)}
			>
				<X2Icon
					bg_color={bg_color}
					text_color={text_color}
				/>
			</Group>

			<Path
				ref={(node) => { refs.current['outer_rect'] = node; }}
				data={`M0,0H${w}V${h}H0Z`}
				fill='transparent'
				stroke={`rgba(${bg_color}, 1)`}
				strokeWidth={10}
				dash={[0, outer_border_length]}
				dashEnabled={true}
			/>
		</>
	)
});

const Digits = forwardRef(({ color, ...props }, ref) => {
	const refs = useRef([]);
	const actual_number = useRef(0);

	const values = props?.values ?? [...Array(10).keys()];
	const value_max_length = props?.value_max_length ?? 1;

	const digits_direction = props?.digits_direction ?? -1;

	const default_props = {
		opacity:0,
	};

	const sliding_props = {
		opacity:0.3,
	};

	const selected_props = {
		opacity:1,
	};

	useImperativeHandle(ref, () => ({
		sliding() {
			values.map((foo, i) => {
				if(i == actual_number.current) return;
				//if(refs.current[`num_${i}`] == undefined) return;

				refs.current[`num_${i}`].to({
					...sliding_props,
					duration:0.2,
					easing:Konva.Easings.StrongEaseInOut,
				});
			});
		},

		selectNumber(number) {
			values.map((foo, i) => {
				if(i == number) return;
				//if(refs.current[`num_${i}`] == undefined) return;

				refs.current[`num_${i}`].to({
					...default_props,
					duration:0.2,
					easing:Konva.Easings.StrongEaseInOut,
				});
			});

			//if(refs.current[`num_${number}`] == undefined) return;
			refs.current[`num_${number}`].to({
				...selected_props,
				duration:0.2,
				easing:Konva.Easings.StrongEaseInOut,
				onFinish:() => {
					actual_number.current = number;
				},
			});
		},
	}), []);

	// each digit fits in a 80 x 120 rect:
	const paths = {
		'_0':"M-0.07,55c-6.57,0-12.49-1.12-17.76-3.36c-5.28-2.24-9.45-5.87-12.54-10.9C-33.46,35.72-35,29.03-35,20.67v-41.34c0-8.36,1.54-15.05,4.63-20.07c3.08-5.02,7.26-8.66,12.54-10.9C-12.56-53.88-6.64-55-0.07-55s12.51,1.12,17.84,3.36c5.32,2.24,9.53,5.87,12.61,10.9C33.46-35.72,35-29.03,35-20.67v41.34c0,8.36-1.54,15.05-4.63,20.07c-3.09,5.03-7.29,8.66-12.61,10.9C12.44,53.88,6.49,55-0.07,55z M-0.07,37.98c4.87,0,8.71-1.39,11.49-4.18c2.78-2.78,4.18-7.16,4.18-13.13v-41.34c0-5.97-1.39-10.35-4.18-13.13c-2.79-2.78-6.62-4.18-11.49-4.18c-4.88,0-8.68,1.39-11.42,4.18c-2.74,2.79-4.1,7.16-4.1,13.13v41.34c0,5.97,1.37,10.35,4.1,13.13C-8.76,36.59-4.95,37.98-0.07,37.98z M-0.07,7.09c-2.09,0-3.76-0.67-5-2.02c-1.25-1.34-1.87-3.01-1.87-5c0-2.09,0.62-3.75,1.87-5c1.24-1.24,2.91-1.87,5-1.87c2.19,0,3.9,0.62,5.15,1.87c1.24,1.25,1.87,2.91,1.87,5c0,1.99-0.62,3.66-1.87,5C3.83,6.42,2.11,7.09-0.07,7.09z",

		'_1':"M3.26,55c-2.4,0-4.62-0.53-6.67-1.58c-2.05-1.05-3.07-2.63-3.07-4.73v-79.99l-4.79,4.8c-0.6,0.4-1.2,0.73-1.8,0.98c-0.6,0.25-1.15,0.38-1.65,0.38c-1.9,0-3.42-0.83-4.57-2.48c-1.15-1.65-1.72-3.47-1.72-5.48c0-1.3,0.3-2.57,0.9-3.83c0.6-1.25,1.5-2.27,2.7-3.08L0.27-53.5c0.6-0.5,1.22-0.87,1.87-1.13C2.79-54.87,3.56-55,4.46-55c2,0,3.92,0.58,5.77,1.73c1.85,1.15,2.77,2.73,2.77,4.73V48.7c0,2.1-1,3.68-3,4.73C8.01,54.47,5.76,55,3.26,55z",

		'_2':"M-25.46,55c-1.7,0-3.55-0.58-5.54-1.73c-2-1.15-3-2.68-3-4.59v-14.6c0-3.91,1.15-7.6,3.45-11.06c2.3-3.46,5.29-6.75,8.99-9.86c3.69-3.11,7.59-6.12,11.68-9.03c4.09-2.91,7.96-5.87,11.61-8.88c3.64-3.01,6.64-6.07,8.99-9.18c2.35-3.11,3.52-6.42,3.52-9.93c0-2.11-0.47-4.19-1.42-6.24c-0.95-2.05-2.45-3.76-4.49-5.12c-2.05-1.35-4.72-2.03-8.01-2.03c-3.59,0-6.72,1.03-9.36,3.08c-2.65,2.06-3.97,5.44-3.97,10.16c0,1.81-0.9,3.46-2.7,4.97c-1.8,1.5-4.29,2.26-7.49,2.26c-2.6,0-4.69-0.75-6.29-2.26c-1.6-1.5-2.4-4.26-2.4-8.28c0-5.72,1.5-10.66,4.49-14.82c3-4.16,6.97-7.35,11.91-9.56C-10.56-53.9-5.29-55,0.3-55c5.79,0,11.23,1.23,16.33,3.69c5.09,2.46,9.21,5.94,12.36,10.46s4.72,9.98,4.72,16.4c0,5.32-1.15,10.13-3.44,14.45c-2.3,4.31-5.24,8.23-8.84,11.74C17.82,5.24,14,8.43,9.96,11.29C5.92,14.14,2.1,16.8-1.5,19.26c-3.59,2.46-6.54,4.89-8.84,7.3c-2.3,2.41-3.44,4.92-3.44,7.52v3.76h41.64c1.7,0,3.15,0.83,4.34,2.48c1.2,1.66,1.8,3.64,1.8,5.94c0,2.31-0.6,4.34-1.8,6.09C31,54.12,29.56,55,27.86,55H-25.46z",

		'_3':"M-0.82,55c-6.07,0-11.27-0.85-15.6-2.54c-4.33-1.69-7.89-3.88-10.67-6.57c-2.79-2.69-4.8-5.62-6.04-8.81c-1.25-3.18-1.87-6.27-1.87-9.25c0-2.99,0.67-5,2.02-6.05c1.34-1.04,3.71-1.57,7.09-1.57c3.98,0,6.64,0.52,7.99,1.57c1.34,1.04,2.01,2.76,2.01,5.15c0,1.79,0.35,3.36,1.04,4.7c0.69,1.34,1.72,2.49,3.06,3.43c1.34,0.95,2.98,1.67,4.93,2.16c1.94,0.5,4.1,0.75,6.49,0.75c5.57,0,9.63-1.24,12.16-3.73c2.54-2.49,3.81-6.62,3.81-12.39v-2.84c0-5.47-1.52-9.2-4.55-11.19C8.01,5.85,4.1,4.85-0.67,4.85c-2.19,0-3.73-0.8-4.63-2.39c-0.9-1.59-1.34-3.33-1.34-5.22c0-1.89,0.45-3.63,1.34-5.22c0.9-1.59,2.39-2.39,4.48-2.39c2.49,0,4.78-0.32,6.87-0.97c2.09-0.65,3.75-2.01,5-4.1c1.24-2.09,1.87-5.27,1.87-9.55c0-4.97-1.17-8.41-3.51-10.3c-2.34-1.89-5.55-2.84-9.63-2.84c-2.39,0-4.43,0.22-6.12,0.67c-1.69,0.45-3.09,1.12-4.18,2.01c-1.1,0.9-1.87,1.94-2.31,3.13c-0.45,1.19-0.67,2.59-0.67,4.18c0,2.19-0.62,3.98-1.87,5.37c-1.25,1.39-3.66,2.09-7.24,2.09c-2.98,0-5.17-0.25-6.57-0.75c-1.39-0.5-2.34-1.37-2.84-2.61c-0.5-1.24-0.75-2.91-0.75-5c0-2.69,0.55-5.52,1.64-8.51c1.09-2.98,2.88-5.82,5.37-8.51c2.49-2.69,5.82-4.85,10-6.49C-11.57-54.18-6.34-55-0.07-55c6.07,0,11.47,0.85,16.19,2.54c4.72,1.69,8.48,4.48,11.27,8.36c2.78,3.88,4.18,9.16,4.18,15.82c0,5.87-1.04,11.05-3.13,15.52c-2.09,4.48-4.98,7.56-8.66,9.25c4.38,1.69,8.01,4.33,10.9,7.91C33.56,7.99,35,12.86,35,19.03v2.84c0,8.06-1.62,14.5-4.85,19.33c-3.23,4.83-7.56,8.34-12.99,10.52C11.74,53.91,5.75,55-0.82,55z",

		'_4':"M14.69,55c-2.39,0-4.6-0.53-6.64-1.58c-2.04-1.05-3.06-2.63-3.06-4.74V31.07h-35.79c-1.89,0-3.48-0.63-4.77-1.88c-1.29-1.25-1.94-2.94-1.94-5.04c0-0.6,0.1-1.25,0.3-1.96c0.2-0.7,0.45-1.4,0.75-2.11l35.34-69.97c0.89-1.9,2.09-3.24,3.58-3.99C3.95-54.62,5.49-55,7.08-55c1.19,0,2.53,0.3,4.03,0.9s2.81,1.48,3.95,2.63c1.14,1.15,1.71,2.48,1.71,3.99c0,0.6-0.25,1.46-0.75,2.56L-12.6,12.87H4.99v-15.8c0-2.21,1.02-3.79,3.06-4.74c2.04-0.95,4.25-1.43,6.64-1.43s4.6,0.48,6.64,1.43c2.04,0.95,3.06,2.53,3.06,4.74v15.8h6.86c2.09,0,3.65,0.96,4.7,2.86c1.04,1.91,1.57,3.96,1.57,6.17c0,2.31-0.65,4.42-1.94,6.32c-1.29,1.91-2.74,2.86-4.32,2.86h-6.86v17.61c0,2.11-1.02,3.69-3.06,4.74C19.28,54.47,17.07,55,14.69,55z",

		'_5':"M0.22,55c-7.56,0-13.98-1.29-19.25-3.88c-5.28-2.59-9.25-5.94-11.94-10.08C-33.66,36.92-35,32.56-35,27.99c0-3.48,0.84-5.82,2.54-7.01c1.69-1.19,4.08-1.79,7.16-1.79c4.08,0,6.72,0.72,7.91,2.16c1.19,1.44,1.79,3.16,1.79,5.15c0,2.49,0.67,4.58,2.01,6.27c1.34,1.69,3.18,2.99,5.52,3.88c2.34,0.9,4.95,1.34,7.84,1.34c4.97,0,8.85-1.34,11.64-4.03c2.78-2.69,4.18-6.87,4.18-12.54v-2.84c0-5.67-1.39-9.87-4.18-12.61c-2.79-2.74-6.57-4.1-11.34-4.1h-22.24c-2.98,0-5.03-0.7-6.12-2.09c-1.1-1.39-1.64-3.23-1.64-5.52c0-0.7,0.02-1.34,0.07-1.94c0.05-0.6,0.12-1.24,0.22-1.94l3.73-39.1c0.1-1.99,0.47-3.53,1.12-4.63c0.65-1.09,1.52-1.64,2.61-1.64h45.97c1.99,0,3.46,0.87,4.4,2.61c0.94,1.74,1.42,3.71,1.42,5.89c0,2.09-0.47,4.03-1.42,5.82c-0.95,1.79-2.41,2.69-4.4,2.69H-9.78l-1.79,24.63H0.07c6.47,0,12.34,1.02,17.61,3.06c5.27,2.04,9.48,5.42,12.61,10.15C33.43,4.58,35,10.82,35,18.58v2.84c0,8.06-1.57,14.58-4.7,19.55c-3.13,4.98-7.34,8.56-12.61,10.75C12.41,53.91,6.59,55,0.22,55z",

		'_6':"M0.07,55c-6.57,0-12.49-1.12-17.76-3.36c-5.28-2.24-9.48-5.87-12.61-10.89c-3.13-5.02-4.7-11.72-4.7-20.07v-41.34c0-8.36,1.59-15.05,4.78-20.07c3.18-5.02,7.49-8.66,12.91-10.89C-11.89-53.88-5.75-55,1.12-55c5.87,0,10.94,0.82,15.22,2.46c4.28,1.64,7.81,3.81,10.6,6.49c2.78,2.69,4.83,5.65,6.12,8.88c1.29,3.23,1.94,6.39,1.94,9.48c0,2.99-0.75,5.07-2.24,6.27c-1.49,1.19-3.78,1.79-6.87,1.79c-2.59,0-4.9-0.5-6.94-1.49c-2.04-0.99-3.06-2.93-3.06-5.82c0-2.29-0.72-4.25-2.16-5.9c-1.44-1.64-3.31-2.91-5.6-3.81c-2.29-0.9-4.83-1.34-7.61-1.34c-5.07,0-9.03,1.39-11.87,4.18c-2.84,2.79-4.25,7.16-4.25,13.13v16.12c2.39-2.88,5.3-4.87,8.73-5.97c3.43-1.09,7.09-1.64,10.97-1.64c5.67,0,10.82,0.95,15.45,2.84c4.63,1.89,8.36,5.08,11.19,9.55C33.58,4.7,35,10.87,35,18.73v1.94c0,8.36-1.54,15.05-4.63,20.07c-3.09,5.03-7.26,8.66-12.54,10.89C12.56,53.88,6.64,55,0.07,55z M0.07,39.03c4.87,0,8.68-1.34,11.42-4.03c2.74-2.69,4.1-7.02,4.1-12.99v-1.94c0-6.17-1.37-10.55-4.1-13.13C8.75,4.35,4.9,3.06-0.07,3.06c-4.68,0-8.43,1.19-11.27,3.58c-2.84,2.39-4.25,6.42-4.25,12.09v3.28c0,5.97,1.39,10.3,4.18,12.99C-8.63,37.69-4.8,39.03,0.07,39.03z",

		'_7':"M-12.16,55c-1.89,0-3.81-0.42-5.75-1.27c-1.94-0.84-3.48-2.02-4.63-3.51c-1.15-1.5-1.72-3.14-1.72-4.93c0-1.2,0.3-2.34,0.9-3.44L15.6-28.1v-9.86H-15.6v12.11c0,1.7-1,3.16-2.99,4.41c-1.99,1.25-4.28,1.87-6.87,1.87c-2.59,0-4.83-0.62-6.72-1.87c-1.89-1.24-2.84-2.71-2.84-4.41v-22.87c0-1.59,0.9-3.04,2.69-4.33c1.79-1.29,3.73-1.94,5.82-1.94h52.98c1.89,0,3.78,0.65,5.67,1.94c1.89,1.3,2.84,2.74,2.84,4.33v16.89c0,1.49-0.4,3.31-1.19,5.45c-0.8,2.14-1.69,4.11-2.69,5.9L-6.49,52.16C-7.49,54.05-9.38,55-12.16,55z",

		'_8':"M0.07,55c-6.57,0-12.49-1.02-17.76-3.06c-5.28-2.04-9.48-5.32-12.61-9.85c-3.13-4.53-4.7-10.47-4.7-17.84v-1.79c0-7.66,1.64-13.78,4.93-18.36c3.28-4.58,7.56-7.81,12.84-9.7c-4.18-1.49-7.61-4.13-10.3-7.91c-2.69-3.78-4.03-8.81-4.03-15.07c0-6.37,1.37-11.47,4.1-15.3c2.74-3.83,6.49-6.64,11.27-8.43C-11.42-54.1-6-55,0.07-55c6.17,0,11.62,0.9,16.34,2.69c4.73,1.79,8.43,4.6,11.12,8.43c2.69,3.83,4.03,8.93,4.03,15.3c0,6.27-1.32,11.29-3.96,15.07c-2.64,3.78-6.1,6.42-10.37,7.91c5.37,1.89,9.68,5.13,12.91,9.7C33.38,8.68,35,14.8,35,22.46v1.79c0,7.36-1.54,13.31-4.63,17.84c-3.09,4.53-7.26,7.81-12.54,9.85C12.56,53.98,6.64,55,0.07,55z M0.07,39.18c4.97,0,8.81-1.42,11.49-4.25s4.03-7.14,4.03-12.91v-2.99c0-5.67-1.37-9.82-4.1-12.46C8.75,3.93,4.95,2.61,0.07,2.61c-4.88,0-8.71,1.32-11.49,3.96c-2.79,2.64-4.18,6.79-4.18,12.46v2.99c0,5.97,1.42,10.32,4.25,13.06C-8.51,37.81-4.7,39.18,0.07,39.18z M0.07-13.21c4.08,0,7.24-0.99,9.48-2.99c2.24-1.99,3.36-5.32,3.36-10c0-4.58-1.12-7.88-3.36-9.93c-2.24-2.04-5.4-3.06-9.48-3.06c-4.08,0-7.24,1.02-9.48,3.06c-2.24,2.04-3.36,5.35-3.36,9.93c0,4.68,1.12,8.01,3.36,10C-7.16-14.2-4.01-13.21,0.07-13.21z",

		'_9':"M-1.04,55c-5.96,0-11.07-0.82-15.34-2.46c-4.27-1.64-7.8-3.81-10.57-6.49c-2.78-2.69-4.82-5.65-6.11-8.88c-1.29-3.23-1.94-6.39-1.94-9.48c0-3.08,0.77-5.2,2.31-6.34c1.54-1.14,3.8-1.72,6.78-1.72c2.68,0,5.04,0.47,7.07,1.42c2.03,0.95,3.05,2.91,3.05,5.9c0,2.29,0.69,4.25,2.08,5.89c1.39,1.64,3.23,2.91,5.51,3.81c2.28,0.9,4.86,1.34,7.74,1.34c4.96,0,8.89-1.39,11.77-4.18c2.88-2.78,4.32-7.16,4.32-13.13V4.55c-2.48,2.89-5.44,4.88-8.86,5.97c-3.43,1.1-7.07,1.64-10.95,1.64c-5.66,0-10.82-0.94-15.49-2.84c-4.67-1.89-8.39-5.07-11.17-9.55C-33.61-4.7-35-10.87-35-18.73v-1.94c0-8.36,1.54-15.05,4.62-20.07c3.08-5.02,7.25-8.66,12.51-10.89C-12.61-53.88-6.7-55-0.15-55s12.49,1.12,17.8,3.36c5.31,2.24,9.53,5.87,12.66,10.89C33.44-35.72,35-29.03,35-20.67v41.34c0,8.36-1.62,15.05-4.84,20.07c-3.23,5.03-7.55,8.66-12.96,10.89C11.79,53.88,5.71,55-1.04,55z M0-3.06c4.77,0,8.56-1.22,11.39-3.66c2.83-2.44,4.24-6.44,4.24-12.01v-3.28c0-5.97-1.39-10.3-4.17-12.99c-2.78-2.69-6.65-4.03-11.62-4.03c-4.87,0-8.66,1.34-11.39,4.03c-2.73,2.69-4.1,7.01-4.1,12.99v1.94c0,6.17,1.36,10.55,4.1,13.14C-8.81-4.35-4.97-3.06,0-3.06z",

		'_-':"M-20.99,8.5c-1.91,0-3.47-0.79-4.69-2.38c-1.22-1.59-1.83-3.6-1.83-6.04c0-2.44,0.61-4.47,1.83-6.12c1.22-1.64,2.78-2.46,4.69-2.46h41.97c1.59,0,3.07,0.82,4.45,2.46c1.38,1.64,2.07,3.68,2.07,6.12c0,2.44-0.69,4.45-2.07,6.04c-1.38,1.59-2.86,2.38-4.45,2.38H-20.99z",

		'_+':"M25.43-6.04c-1.38-1.64-2.86-2.46-4.45-2.46H8.5v-12.49c0-1.59-0.79-3.07-2.38-4.45c-1.59-1.38-3.6-2.07-6.04-2.07c-2.44,0-4.47,0.69-6.12,2.07c-1.64,1.38-2.46,2.86-2.46,4.45V-8.5h-12.49c-1.91,0-3.47,0.82-4.69,2.46c-1.22,1.64-1.83,3.68-1.83,6.12c0,2.44,0.61,4.45,1.83,6.04c1.22,1.59,2.78,2.38,4.69,2.38H-8.5v12.49c0,1.91,0.82,3.47,2.46,4.69c1.64,1.22,3.68,1.83,6.12,1.83c2.44,0,4.45-0.61,6.04-1.83c1.59-1.22,2.38-2.78,2.38-4.69V8.5h12.49c1.59,0,3.07-0.79,4.45-2.38c1.38-1.59,2.07-3.6,2.07-6.04C27.5-2.36,26.81-4.39,25.43-6.04z",
	};

	const box_w = value_max_length * digit_w;
	const box_h = values.length * digit_h;

	return (
		<Group
			x={digit_w / 2 + (props?.digits_props?.x ?? 0)}
			y={digit_h / 2 + (props?.digits_props?.y ?? 0)}
		>
			<Rect
				width={box_w}
				height={box_h}
				fill='rgba(100, 50, 0, 0)'
			/>

			{values.map((value, t) => {
				const digit_props = (t == 0) ? selected_props : default_props;
				const single_digits_in_digit = `${value}`.split("");

				return (
					<Group
						key={`num_${t}`}
						ref={(node) => { refs.current[`num_${t}`] = node; }}
						{...digit_props}
						x={(box_w - single_digits_in_digit.length * digit_w) / 2}
						y={(digits_direction < 0) ? t * digit_h : (values.length - 1 - t) * digit_h}
					>
						{single_digits_in_digit.map((d, index) => {
							return (
								<Path
									key={`num_${t}_${index}`}
									data={paths[`_${d}`]}
									x={digit_w * index}
									fill={`rgba(${color.join(", ")}, 1)`}
								/>
							)
						})}
					</Group>
				)
			})}
		</Group>
	)
});

const Button = ({ color, ...props }) => {
	const default_props = {
		stroke:`rgba(${color.join(", ")}, 1)`,
		strokeWidth:3,
		fill:'transparent',
	};

	return (
		<Group
			{...props?.button_props ?? {}}
		>
			<Rect
				x={-40}
				y={-25}
				width={80}
				height={50}
			/>
		
			<Circle
				{...default_props}
				radius={20}
			/>

			<Line
				{...default_props}
				class="st0"
				points={[ -9, 4.5, 0, -4.5, 9, 4.5 ]}
			/>
		</Group>
	)
};

const DigitPicker = forwardRef(({ position, ...props }, ref) => {
	const refs = useRef([]);
	const actual_number = useRef(0);
	const actual_loop = useRef(0);

	const team = props?.team ?? 0;

	const values = props?.values ?? [...Array(10).keys()];
	const value_max_length = props?.value_max_length ?? 1;

	const digits_direction = props?.digits_direction ?? -1;

	const max_h = values.length * digit_h;

	const box_mid_y = (digit_box_h - digit_h) / 2;
	const init_y = (digits_direction < 0) ? 0 : -max_h;

	const moveTo = (direction) => {
		actual_loop.current = 0;

		if(direction > 0) {
			actual_number.current++;
			if(actual_number.current > values.length - 1) {
				actual_number.current = 0;
				actual_loop.current = (digits_direction < 0) ? 1 : -1;
			}
		} else {
			actual_number.current--;
			if(actual_number.current < 0) {
				actual_number.current = values.length - 1;
				actual_loop.current = (digits_direction < 0) ? -1 : 1;
			}
		}

		selectDigit();
	};

	const getFinalY = () => {
		const block_displacement = (digits_direction < 0) ? actual_loop.current * max_h : -actual_loop.current * max_h;
		return digits_direction * (actual_number.current * digit_h + block_displacement) + box_mid_y + init_y;
	}

	const selectDigit = () => {
		refs.current['selected_digit_0'].selectNumber(actual_number.current);
		refs.current['selected_digit_1'].selectNumber(actual_number.current);
		refs.current['selected_digit_2'].selectNumber(actual_number.current);

		if(props.onChange != undefined) {
			props.onChange(team, position, actual_number.current);
		}

		refs.current['digit'].to({
			y:getFinalY(),
			duration:0.2,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				actual_loop.current = 0;

				if(refs.current['digit'] == undefined) return;

				refs.current['digit'].setAttrs({
					y:getFinalY(),
				});
			},
		});
	};

	useImperativeHandle(ref, () => ({
		reset(new_digit = 0) {
			actual_loop.current = 0;
			actual_number.current = new_digit;
			selectDigit();
		},
	}), []);

	const onDragStart = (e) => {
		refs.current['buttons'].to({
			opacity:0.2,
			duration:0.4,
			easing:Konva.Easings.StrongEaseInOut,
		});

		refs.current['selected_digit_0'].sliding();
		refs.current['selected_digit_1'].sliding();
		refs.current['selected_digit_2'].sliding();
	};

	const onDrag = (e) => {
		refs.current['digit'].setAttrs({
			x:0,
			y:Math.min(max_h, Math.max(-2 * max_h + digit_box_h, refs.current['digit'].attrs.y)),
		});
	};

	const onDragEnd = () => {
		refs.current['buttons'].to({
			opacity:1,
			duration:1,
			easing:Konva.Easings.StrongEaseInOut,
		});

		const y = refs.current['digit'].attrs.y;
		const abs_y = Math.abs(y);

		if(abs_y > max_h) {
			actual_loop.current = 1;
		} else if(y > 0) {
			actual_loop.current = -1;
		} else {
			actual_loop.current = 0;
		}

		const selected_number_coord = Math.round((Math.abs(y - digit_h / 2)) / digit_h) % values.length;

		actual_number.current = ((digits_direction < 0) ? selected_number_coord : (values.length - selected_number_coord)) % values.length;
		if(actual_number.current == 0) actual_loop.current = 0;
		if(actual_loop.current < 0) actual_number.current = (values.length - actual_number.current) % values.length;

		selectDigit();
	};

	return (
		<Group
			{...props?.item_props ?? {}}
		>
			<Group
				x={-1 * digit_box_w * (value_max_length - 1) / 2}
				clipFunc={(ctx) => {
					ctx.rect(0, 0, value_max_length * digit_box_w, digit_box_h);
				}}
			>
				<Group
					ref={(node) => { refs.current['digit'] = node; }}
					draggable={true}
					onDragStart={onDragStart}
					onDragMove={onDrag}
					onDragEnd={onDragEnd}
					y={box_mid_y + init_y}
				>
					<Digits
						ref={(node) => { refs.current['selected_digit_0'] = node; }}
						digits_direction={digits_direction}
						color={props.text_color}
						digits_props={{ y:-max_h + ((digits_direction < 0) ? 0 : digit_h) }}
						values={values}
						value_max_length={value_max_length}
					/>

					<Digits
						ref={(node) => { refs.current['selected_digit_1'] = node; }}
						digits_direction={digits_direction}
						color={props.text_color}
						digits_props={{ y:((digits_direction < 0) ? 0 : digit_h) }}
						values={values}
						value_max_length={value_max_length}
					/>

					<Digits
						ref={(node) => { refs.current['selected_digit_2'] = node; }}
						digits_direction={digits_direction}
						color={props.text_color}
						digits_props={{ y:max_h + ((digits_direction < 0) ? 0 : digit_h) }}
						values={values}
						value_max_length={value_max_length}
					/>
				</Group>
			</Group>

			<Group
				ref={(node) => { refs.current['buttons'] = node; }}
			>
				<Button
					color={props.main_color}
					button_props={{
						x:digit_box_w / 2,
						y:25,
						onClick:() => moveTo((digits_direction < 0) ? -1 : 1),
						onTap:() => moveTo((digits_direction < 0) ? -1 : 1),
					}}
				/>

				<Button
					color={props.main_color}
					button_props={{
						x:digit_box_w / 2,
						y:digit_box_h - 25,
						rotation:180,
						onClick:() => moveTo((digits_direction < 0) ? 1 : -1),
						onTap:() => moveTo((digits_direction < 0) ? 1 : -1),
					}}
				/>
			</Group>
		</Group>
	)
});

const GuessScoreCanvas = forwardRef((props, ref) => {
	const stage = props.stage;

	const [ full_loaded, setFullLoaded ] = useState(false);

	const indexed_images = props?.indexed_images ?? {};

	const is_touch_device = ((window?.platform ?? 'desktop') == 'mobile') ? true : false;
	const prevent_default = (is_touch_device) ? false : null;

	const is_builder = props?.is_builder ?? false;
	const is_test = props?.is_test ?? false;
	const bracket = props?.bracket ?? false;
	const delay_to_tip = 1.8; // in seconds
	
	const teams = props?.teams ?? [];
	const type = (bracket !== false) ? 'only_winner' : (stage?.guess_score_type ?? 'result');
	const score_num_digits = (type == 'result') ? parseInt(stage?.guess_score_num_digits ?? 1) : 1;
	const display_outter_border = stage?.display_outter_border ?? true;
	const natural_direction = stage?.guess_score_natural_direction ?? true;
	const digits_direction = (natural_direction === false) ? -1 : 1;	// 1: increasing numbers down to up | -1: increasing numbers up to down
	const has_step_instructions = (isNotEmpty(stage, 'guess_score_step_2_instructions', [""])) ? true : false;
	const tint_winner = stage?.guess_score_tint_winner ?? true;

	const step_interval = parseInt(stage?.guess_score_step_interval ?? '2');
	const max_interval = parseInt(stage?.guess_score_max_interval ?? '3');

	const matches = useRef(props?.matches ?? (stage?.guess_score_matches ?? []));
	const rounds = props?.rounds ?? (stage?.guess_score_rounds ?? []);
	const num_rounds = parseInt(stage?.guess_score_num_rounds ?? 4);

	const bracket_h = useRef(0);
	const bracket_ref = useRef(0);
	const final_size = useRef(real_board_size);
	const throttle_tip = useRef(null);

	let real_used_size = { ...real_board_size };
	if(bracket !== false) {
		real_used_size.h = real_board_size.w;
		bracket_h.current = real_board_size.w;
		//bracket_h.current = calcBracketHeight(num_rounds, true, false);
	}

	const buttons = props?.buttons ?? false;
	const header = props?.header ?? false;

	let intervals = [...Array(10).keys()];
	if(['margin', 'matches'].includes(type)) intervals = getAvailableIntervals(step_interval, max_interval);

	const user_answer = props?.user_answer ?? {};

	const is_editing_display = props?.is_editing ?? false;

	const bg_color = stage?.bg_color ?? 'rgba(255, 255, 255, 1)';
	const text_color = colorToArray(stage.text_color);
	const main_color = colorToArray(stage.main_color);

	const x2_color_rgba = stage?.guess_score_x2_color ?? `rgba(${text_color}, 1)`;
	const x2_color = colorToArray(x2_color_rgba);
	const x2_text_color = colorToArray((isColorBright(RGB_2_HEX(x2_color_rgba))) ? 'rgba(0, 0, 0, 1)' : 'rgba(255, 255, 255, 1)');

	const default_match_results = { team_1:0, team_2:0 };

	const getIntervalIndex = (int) => {
		if(!['margin', 'matches'].includes(type)) return;

		const i_index = intervals.indexOf(int);
		return i_index;
	};

	const generateUserBets = () => {
		const bets = [];
		matches.current.map((match) => {
			switch(type) {
				case 'margin':
				case 'matches':
					bets.push({
						winner:(is_editing_display) ? (user_answer[match.id]?.winner ?? '') : '',
						interval:(is_editing_display) ? (user_answer[match.id]?.interval ?? intervals[0]) : intervals[0],
					});
					break;

				case 'only_winner':
					bets.push({
						winner:'',
					});
					break;

				case 'result':
				default:
					bets.push({
						[match.id_1]:(is_editing_display) ? (user_answer[match.id]?.id_1 ?? 0) : 0,
						[match.id_2]:(is_editing_display) ? (user_answer[match.id]?.id_2 ?? 0) : 0,
					});
					break;
			}
		});

		return bets;
	};

	const getCurrentMatchIndex = () => {
		if(props.current_match == undefined) return 0;
		
		let index = 0;
		matches.current.map((match, m) => {
			if(match.id == props.current_match) index = m;
		});
		return index;
	}

	const stage_ref = useRef();
	const match_results = useRef(default_match_results);
	const current_match = useRef(getCurrentMatchIndex());
	const current_round = useRef(1);
	const matches_in_prev_round = useRef(0);
	const all_matches_answered = useRef(false);
	const user_bets = useRef(generateUserBets());
	const execute_tip = useRef(false);
	const execute_tip_arrows = useRef(true);
	const tip_playing = useRef(false);
	const is_transitioning = useRef(false);
	
	const fine_tune_scale = 0.9;

	const team_w = real_used_size.w / 2;
	const team_h = real_used_size.h / 2;
	const logo_size = fine_tune_scale * ((bracket !== false) ? (real_used_size.w / 4) : (real_used_size.w / 3));
	const logo_y = (bracket !== false) ? 15 : 36;
	const text_y = logo_size + logo_y + ((bracket !== false) ? -75 : 0);
	const text_w = team_w - 2 * text_padding;
	const text_h = team_h - text_y;
	const tip_text_h = (bracket !== false) ? 40 : (text_h + 2 * text_padding);
	const total_tip_arrows = 3;
	const font_size = fine_tune_scale * 38;
	const line_height = 55 / font_size;
	const digits_final_y = 325 + text_padding / 2;
	const digits_final_step_1_y = digits_final_y + ((['margin', 'matches'].includes(type)) ? text_h : 0);
	const change_round_duration = 2; // in sec

	const separator_w = 20;
	const separator_h = 195;
	const digits_separator_props = {
		y:(digit_box_h) / 2,
		points:[ 0, 0, separator_w, 0, separator_w, separator_h, 0, separator_h ],
		strokeWidth:3,
		stroke:`rgba(${text_color.join(", ")}, 0.3)`,
		offsetX:separator_w / 2,
		offsetY:separator_h / 2,
	};

	const separator_line_w = (score_num_digits < 3) ? separator_w * 2 : separator_w;

	const refs = useRef([]);
	const size = useRef({ w:real_used_size.w, h:real_used_size.h });

	const default_text_props = {
		fontSize:font_size,
		lineHeight:line_height,
		fill:`rgba(${text_color.join(", ")}, 1)`,
		fontStyle:'bold',
	};

	const default_transition_props = {
		duration:(['only_winner'].includes(type)) ? 0.7 : 1,
		easing:Konva.Easings.StrongEaseInOut,
	};

	const fast_transition_props = {
		duration:0.5,
		easing:Konva.Easings.StrongEaseInOut,
	};

	const digits_group_props_default = (['margin', 'matches'].includes(type)) ? {
		x:real_used_size.w / 2,
		offsetX:real_used_size.w / 2,
	} : {};

	const teams_group_props_default = (['margin', 'matches'].includes(type)) ? {
		x:real_used_size.w / 2,
		y:team_h * 1.15,
		offsetX:real_used_size.w / 2,
		offsetY:team_h,
	} : {};

	const group_props_off = (['margin', 'matches'].includes(type)) ? {
		scaleX:0.7,
		scaleY:0.7,
	} : {};

	const group_props_on = (['margin', 'matches'].includes(type)) ? {
		scaleX:1,
		scaleY:1,
		opacity:1,
	} : {};

	const winner_team_props = {
		scaleX:1,
		scaleY:1,
		opacity:1,
	};

	const loser_team_props = {
		scaleX:0.5,
		scaleY:0.5,
		opacity:0.3,
	};

	const textFitter = () => {
		if(refs.current['layer'] == undefined) return;

		let all_teams_words = [];
		matches.current.map((match) => {
			const team_1 = getTeamInfoByID(teams, match.id_1);
			const team_2 = getTeamInfoByID(teams, match.id_2);

			if(isNotEmpty(team_1, 'name')) all_teams_words = [ ...all_teams_words, ...getTeamName(team_1).split(" ")];
			if(isNotEmpty(team_2, 'name')) all_teams_words = [ ...all_teams_words, ...getTeamName(team_2).split(" ")];
		});

		const text_helper = new Konva.Text({
			...default_text_props,
			text:all_teams_words.join("\n"),
			opacity:0,
		});

		refs.current['text_helper'] = text_helper;

		refs.current['layer'].add(text_helper);
		const max_text_width = refs.current['text_helper'].getSelfRect().width;
		refs.current['text_helper'].destroy();

		const text_scale = Math.min(1, (text_w / max_text_width));

		[...Array(2).keys()].map((t) => {
			refs.current[`team_${t + 1}_name`].setAttrs({
				fontSize:font_size * text_scale,
				lineHeight:1.1 * text_scale,
				opacity:1,
			});
		});

		refs.current['layer'].draw();
	};

	const gameInit = () => {
		if(props.adaptToContainer != undefined) {
			props.adaptToContainer((size_received) => {
				size.current.w = size_received;
				
				const canvas_scale = size.current.w / real_board_size.w;
				size.current.h = (bracket !== false) ? (size_received / 2 + bracket_h.current * canvas_scale) : size_received;

				final_size.current.h = size.current.h / canvas_scale;

				setTimeout(() => {
					textFitter();
					mountBoard();
				}, 0);

				generateCache();
				setFullLoaded(true);
			});
		} else {
			setFullLoaded(true);
		}
	};

	const resetDigits = () => {
		if(['margin', 'matches'].includes(type)) {
			const digit = getIntervalIndex(user_bets.current[current_match.current].interval);

			if(refs.current[`digit_0_0`] == undefined) return;
			refs.current[`digit_0_0`].reset(digit);
			return;
		}

		[...Array(2).keys()].map((t) => {
			const team = getTeamInfo(t + 1);
			if(team === false) return;

			const digit_parts = getDigitsInNumber(user_bets.current[current_match.current][team.id]);

			digit_parts.map((digit, i) => {
				if(refs.current[`digit_${t}_${i}`] == undefined) return;
				refs.current[`digit_${t}_${i}`].reset(digit);
			});
		});
	};

	const resetGame = () => {
		match_results.current = default_match_results;
		all_matches_answered.current = false;
		user_bets.current = generateUserBets();
		clearTimeout(throttle_tip.current);
		setFullLoaded(false);
		gameInit();
		setTimeout(() => {
			resetDigits();
		}, 0);
	};

	const preventLeaving = (e) => {
		e.returnValue = "There are unsaved changes. Leave now?";
	};

	useEffect(() => {
		resetGame();
	}, [
		stage.guess_score_num_digits,
	]);

	useEffect(() => {
		generateCache();
	}, [
		stage.bg_color,
		stage.text_color,
		stage.main_color,
	]);

	useEffect(() => {
		resetDigits();
		generateCache();
	}, [
		stage.guess_score_matches,
		stage.guess_score_natural_direction,
	]);

	useEffect(() => {
		resetGame();

		if(!is_builder) window.addEventListener('beforeunload', preventLeaving);

		return () => {
			if(!is_builder) window.removeEventListener('beforeunload', preventLeaving);
		};
	}, []);

	const getTeamInfo = (index) => {
		if(matches.current?.[current_match.current]?.[`id_${index}`] == undefined) return false;
		const team_info = getTeamInfoByID(teams, matches.current[current_match.current][`id_${index}`]);
		if(team_info == undefined) return false;
		return team_info;
	};

	const generateCache = () => {
		if(refs.current['layer'] == undefined) return;
		refs.current['bg'].cache(default_cache_props);

		[...Array(2).keys()].map((t) => {
			const team_index = t + 1;
			refs.current[`team_${team_index}_logo`].cache(default_cache_props);
			refs.current[`team_${team_index}_name`].cache(default_cache_props);
		});

		if(refs.current['round_change_bg'] != undefined) {
			refs.current['round_change_bg'].cache(default_cache_props);
		}

		if(refs.current['tip_instructions'] != undefined) {
			refs.current['tip_instructions'].cache(default_cache_props);

			[...Array(total_tip_arrows).keys()].map((arrow) => {
				const arrow_key = `effect_tip_${arrow + 1}`;
				refs.current[arrow_key].cache(default_cache_props);
			});
		}

		refs.current['layer'].draw();
	};

	const getDigitsInNumber = (num) => {
		// we convert the digit into an array, one digit per position (i.e. "[1, 3, 8]" for "138"):
		const digit_parts = (`${num}`).split("");

		// we can't use the variable "score_num_digits" because somehow it gets
		// rewritten when changed the number of digits from builder (so weird):
		const num_digits_t = parseInt(stage?.guess_score_num_digits ?? 1);

		// we fill the remaining empty positions with 0 to ensure we have the expected number of digits
		// (i.e. "[0, 3, 8]" for "38"):
		while(digit_parts.length < num_digits_t) digit_parts.unshift(0);

		return digit_parts;
	};

	const changeDigit = (team, position, digit) => {
		const digit_parts = getDigitsInNumber(match_results.current[`team_${team}`]);

		// we need the digits in units-tens-hundreds to take advantage of 10-based exponents
		// (0 for units, 1 for tens, 2 for hundreds), so we need to reverse the actual number
		// (i.e. "[8, 3, 1]" for "138"):
		digit_parts.reverse();

		// change the number n-position with the new digit:
		digit_parts[position] = digit;

		let new_digit = 0;
		digit_parts.map((d, i) => {
			new_digit += parseInt(d) * Math.pow(10, i);	// decimal base rules! :)
		});

		match_results.current[`team_${team}`] = new_digit;
	};

	const getDirection = (t) => {
		return 2 * t - 1;
	};

	const getLogoProps = (t, ini = true) => {
		if(ini && (!is_builder || is_test)) {
			// not visible:
			return {
				x:getDirection(t) * 2 * team_w,
				rotation:getDirection(t) * (Math.random() * 60 - 30),
			};
		}

		// visible:
		return {
			x:team_w / 2,
			rotation:0,
		};
	};

	const getNameProps = (t, ini = true) => {
		if(ini) {
			// not visible:
			return {
				x:getDirection(t) * 2 * team_w,
				opacity:0,
			};
		}

		// visible:
		return {
			x:text_padding,
			opacity:1,
		};
	};

	const mountBoard = () => {
		const delay = (bracket !== false) ? 1.2 * change_round_duration : 0;

		if(bracket !== false) displayNextRoundMessage();

		setTimeout(() => {
			[...Array(2).keys()].map((team) => {
				const team_index = team + 1;
	
				if(refs.current[`team_${team_index}_logo`] == undefined) return;
	
				refs.current[`team_${team_index}_logo`].cache(default_cache_props);
				refs.current[`team_${team_index}_name`].cache(default_cache_props);

				setTimeout(() => {
					if(refs.current[`team_${team_index}_logo`] == undefined) return;
	
					refs.current[`team_${team_index}_logo`].to({
						...getLogoProps(team, false),
						...default_transition_props,
					});
				}, (team * 100));
	
				if(team == 1 && (ensureBoolean(matches.current[current_match.current]?.x2 ?? false))) {
					setTimeout(() => {
						refs.current['x2'].display();
					}, (2 * fast_transition_props.duration));
				}

				if(header !== false && header.current.updateCustomDescription != undefined) {
					header.current.updateCustomDescription(matches.current[current_match.current]?.description ?? "");
				}

				if(refs.current[`team_${team_index}_name`] != undefined) {
					refs.current[`team_${team_index}_name`].to({
						...getNameProps(team, false),
						...default_transition_props,
						onFinish:() => {
							if(bracket !== false && refs.current['digits_tapper'] != undefined) {
								refs.current['digits_tapper'].setAttrs({
									y:real_used_size.h * 1.1,
								});
							}

							if(tip_playing.current) return;

							clearTimeout(throttle_tip.current);
							throttle_tip.current = setTimeout(() => {
								showTip();
							}, (delay_to_tip * 1000));
						},
					});
				}
			});
		}, (delay * 1000));
	};

	const changeTeam = (team, is_prev = false, delay = 0, cb, is_final = false) => {
		const team_index = team + 1;
		const team_info = getTeamInfo(team_index);

		setTimeout(() => {
			if(refs.current[`team_${team_index}_logo`] == undefined) return;

			if(refs.current[`winner_highlight_${team_index}`] != undefined) {
				refs.current[`winner_highlight_${team_index}`].to({
					opacity:0,
					...default_transition_props,
				});
			}

			const team_props = getTeamInfo(team_index);

			let team_default_props = { ...winner_team_props };

			if(is_prev && ['margin', 'matches'].includes(type)) {
				refs.current['teams_c'].setAttrs({
					...group_props_off,
					y:team_h * ((has_step_instructions) ? 0.85 : 0.95),
				});

				if(team_props.id != user_bets.current[current_match.current].winner) {
					team_default_props = { ...loser_team_props };
				}
			}

			refs.current[`team_${team_index}_logo`].to({
				...getLogoProps(team),
				...default_transition_props,
				onFinish:() => {
					if(is_final) return;

					setTimeout(() => {
						if(refs.current[`team_${team_index}_logo`] == undefined) return;

						const img_key = `logo_${matches.current[current_match.current][`id_${team_index}`]}`;
						const img = (isNotEmpty(indexed_images, img_key)) ? indexed_images[img_key] : false;

						const image_scale = (img !== false) ? Math.min((logo_size / img.image_w), (logo_size / img.image_h)) : 1;

						if(bracket === false && has_step_instructions && refs.current['step_instructions'] != undefined) {
							refs.current['step_instructions'].setAttrs({
								text:stage.guess_score_step_1_instructions
							});
						}

						refs.current[`team_${team_index}_logo`].setAttrs({
							...team_default_props,
							fillPatternImage:img.fillPatternImage,
							fillPatternScaleX:image_scale,
							fillPatternScaleY:image_scale,
							fill:team_info?.color ?? `rgba(${text_color.join(", ")}, 1)`,
							fillPriority:(img !== false) ? 'pattern' : 'fill',
						});

						const team_custom_color = (isNotEmpty(team_props, 'color')) ? colorToArray(team_props.color) : false;
						const average_team_color = `${img?.avg_color?.r ?? 0}, ${img?.avg_color?.g ?? 0}, ${img?.avg_color?.b ?? 0}`;
						const team_color = (team_custom_color !== false) ? `${team_custom_color[0]}, ${team_custom_color[1]}, ${team_custom_color[2]}` : average_team_color;

						if(bracket !== false && refs.current[`team_${team_index}_highlight`] != undefined) {
							refs.current[`team_${team_index}_highlight`].setAttrs({
								fillRadialGradientColorStops:[ 0, `rgba(${team_color}, 0.5)`, 0.6, `rgba(${team_color}, 0)` ],
								opacity:0,
							});
						} else if(refs.current[`winner_highlight_${team_index}`] != undefined) {
							refs.current[`winner_highlight_${team_index}`].setAttrs({
								fillRadialGradientColorStops:[ 0, `rgba(${team_color}, 0.5)`, 1, `rgba(${team_color}, 0)` ],
							});

							refs.current[`winner_highlight_${team_index}`].to({
								opacity:1,
								...default_transition_props,
							});
						}

						refs.current[`team_${team_index}_c`].setAttrs({
							team_id:team_info.id,
						});

						refs.current[`team_${team_index}_logo`].cache(default_cache_props);

						refs.current[`team_${team_index}_logo`].to({
							...getLogoProps(team, false),
							...default_transition_props,
							onFinish:() => {
								if(team_index == 2) is_transitioning.current = false;
								if(cb != undefined) cb();
							},
						});
					}, (delay * 1000));
				}
			});
		}, (team * 100));

		if(refs.current[`team_${team_index}_name`] != undefined) {
			refs.current[`team_${team_index}_name`].to({
				...getNameProps(team),
				...default_transition_props,
				onFinish:() => {
					if(is_final) return;

					setTimeout(() => {
						if(refs.current[`team_${team_index}_name`] == undefined) return;

						refs.current[`team_${team_index}_name`].setAttrs({
							text:getTeamName(team_info),
						});

						refs.current[`team_${team_index}_name`].cache(default_cache_props);

						refs.current[`team_${team_index}_name`].to({
							...getNameProps(team, false),
							...default_transition_props,
						});
					}, (delay * 1000));
				}
			});
		}
	};

	const displayNextRoundMessage = () => {
		if(bracket === false) return;
		if(is_builder && !is_test) return;
		if(rounds[current_round.current - 1] == undefined) return;

		refs.current['round_change_text'].setAttrs({
			text:rounds[current_round.current - 1].title,
			x:real_used_size.w + 100,
		});

		/* Hidden instructions message */
		refs.current['tip_instructions'].to({
			opacity:0,
			duration:0.4,
		});

		const text_width = refs.current['round_change_text'].getSelfRect().width;

		refs.current['round_change_text'].cache(default_cache_props);

		if(refs.current['round_change_bg'] == undefined) return;

		refs.current['round_change_bg'].to({
			opacity:1,
			duration:0.4 * change_round_duration,
			onFinish:() => {
				setTimeout(() => {
					if(refs.current['round_change_bg'] == undefined) return;

					refs.current['round_change_bg'].to({
						opacity:0,
						duration:0.4 * change_round_duration,
						onFinish:() => {
							if(refs.current['tip_instructions'] == undefined) return;
							// Hidden instructions message
							refs.current['tip_instructions'].to({
								opacity:1,
								duration:0.4,
							});
						},
					});
				}, (0.4 * change_round_duration * 1000));
			},
		});

		setTimeout(() => {
			if(refs.current['round_change_text'] == undefined) return;

			refs.current['round_change_text'].to({
				x:-(text_width + 100),
				duration:change_round_duration,
			});
		}, (0.3 * change_round_duration * 1000));
	};

	const changeRound = () => {
		if(bracket === false) {
			navigateToAnotherMatch('next');
			return;
		}

		if(current_match.current == Math.pow(2, num_rounds - current_round.current) + matches_in_prev_round.current - 1) {
			matches_in_prev_round.current += Math.pow(2, num_rounds - current_round.current);
			current_round.current = current_round.current + 1;

			navigateToAnotherMatch('next', () => {}, change_round_duration);

			displayNextRoundMessage();
			return;
		}

		navigateToAnotherMatch('next');
	};

	const showTip = () => {
		if(bracket === false) return;
		if(is_builder && !is_test) return;
		if(refs.current['tip_instructions'] == undefined) return;
		if(execute_tip.current === true) return;
		if(current_match.current == matches.current.length - 1) return;

		execute_tip.current = true;
		execute_tip_arrows.current = true;
		tip_playing.current = false;

		refs.current['tip_instructions'].to({
			y:0,
			duration:1,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				animateTipArrows();
			},
		});

		instructionsTip();
	};

	const hideTip = () => {
		if(bracket === false) return;

		execute_tip.current = false;
		execute_tip_arrows.current = false;

		if(refs.current['tip_instructions'] == undefined) return;

		refs.current['tip_instructions'].to({
			y:tip_text_h,
			duration:1,
			easing:Konva.Easings.StrongEaseInOut,
		});

		setTimeout(() => {
			[...Array(total_tip_arrows).keys()].map((arrow) => {
				const arrow_key = `effect_tip_${arrow + 1}`;
				if(refs.current[arrow_key] == undefined) return;
				
				refs.current[arrow_key].to({
					opacity:0,
					duration:0.5,
					easing:Konva.Easings.StrongEaseInOut,
				});
			});
		}, 100);
	};

	const animateTipArrows = () => {
		if(execute_tip_arrows.current === false) return;
		
		[...Array(total_tip_arrows).keys()].map((arrow) => {
			const arrow_key = `effect_tip_${arrow + 1}`;

			if(refs.current[arrow_key] == undefined) return;

			refs.current[arrow_key].setAttrs({
				scaleX:1,
				scaleY:1,
				opacity:0,
				y:0,
			});

			setTimeout(() => {
				if(execute_tip_arrows.current === false) return;
				if(refs.current[arrow_key] == undefined) return;

				refs.current[arrow_key].to({
					scaleX:1.5,
					scaleY:1.5,
					y:15,
					opacity:1,
					duration:1,
					easing:Konva.Easings.StrongEaseInOut,
					onFinish:() => {
						if(execute_tip_arrows.current === false) return;
						if(refs.current[arrow_key] == undefined) return;

						refs.current[arrow_key].to({
							opacity:0,
							duration:0.5,
							easing:Konva.Easings.StrongEaseInOut,
							onFinish:() => {
								if(arrow == total_tip_arrows - 1) {
									animateTipArrows();
								}
							},
						});
					},
				});
			}, (arrow * 120));
		});
	};

	const instructionsTip = (direction = 1) => {
		if(bracket === false) return;
		if(is_builder && !is_test) return;
		if(execute_tip.current === false) return;
		if(refs.current['team_1_logo'] == undefined) return;
		if(refs.current['team_2_logo'] == undefined) return;
		if(tip_playing.current == true) return;
		tip_playing.current = true;

		const final_rotation = 5;

		const delay = 2000;

		const anim_rotation_props = {
			duration:0.15,
			easing:Konva.Easings.StrongEaseInOut,
		};

		const anim_y_props = {
			duration:0.3,
			easing:Konva.Easings.StrongEaseInOut,
		};

		const orig_y = refs.current['team_1_logo'].attrs.orig_y;

		refs.current['team_1_logo'].to({
			y:orig_y - 10,
			...anim_y_props,
			onFinish:() => {
				setTimeout(() => {
					if(execute_tip.current === false) return;
					if(refs.current['team_1_logo'] == undefined) return;

					refs.current['team_1_logo'].to({
						y:orig_y,
						...anim_y_props,
					});
				}, (anim_rotation_props.duration * 1000));
			},
		});

		refs.current['team_1_logo'].to({
			rotation:-final_rotation,
			...anim_rotation_props,
			onFinish:() => {
				if(execute_tip.current === false) return;
				if(refs.current['team_1_logo'] == undefined) return;

				refs.current['team_1_logo'].to({
					rotation:final_rotation,
					...anim_rotation_props,
					onFinish:() => {
						if(execute_tip.current === false) return;
						if(refs.current['team_1_logo'] == undefined) return;

						refs.current['team_1_logo'].to({
							rotation:0,
							...anim_rotation_props,
						});
					},
				});
			},
		});

		setTimeout(() => {
			if(refs.current['team_2_logo'] == undefined) return;

			refs.current['team_2_logo'].to({
				y:orig_y - 10,
				...anim_y_props,
				onFinish:() => {
					setTimeout(() => {
						if(execute_tip.current === false) return;
						if(refs.current['team_1_logo'] == undefined) return;

						refs.current['team_2_logo'].to({
							y:orig_y,
							...anim_y_props,
						});
					}, (anim_rotation_props.duration * 1000));
				},
			});

			refs.current['team_2_logo'].to({
				rotation:final_rotation,
				...anim_rotation_props,
				onFinish:() => {
					if(execute_tip.current === false) return;
					if(refs.current['team_2_logo'] == undefined) return;

					refs.current['team_2_logo'].to({
						rotation:-final_rotation,
						...anim_rotation_props,
						onFinish:() => {
							if(execute_tip.current === false) return;
							if(refs.current['team_2_logo'] == undefined) return;

							refs.current['team_2_logo'].to({
								rotation:0,
								...anim_rotation_props,
								onFinish:() => {
									setTimeout(() => {
										tip_playing.current = false;
										instructionsTip(-1 * direction);
									}, delay);
								},
							});
						},
					});
				},
			});
		}, 100);
	};

	const selectWinner = (team_index) => {
		if(!['margin', 'only_winner'].includes(type)) return;
		if(is_builder && !is_test) return;

		// to prevent misterious double execution of selectWinner on Android...
		if(is_transitioning.current) return;
		is_transitioning.current = true;

		refs.current['x2'].hide();

		hideTip();

		const team_id = refs.current[`team_${team_index}_c`].attrs.team_id;

		user_bets.current[current_match.current].winner = team_id;

		if(bracket !== false) {
			bracket_ref.current.selectMatchWinner(current_match.current, team_index);

			if(tint_winner && is_touch_device && bracket !== false) {
				refs.current[`team_${team_index}_highlight`].to({
					opacity:0.7,
					duration:0.3,
					easing:Konva.Easings.StrongEaseInOut,
					onFinish:() => {
						if(refs.current[`team_${team_index}_highlight`] == undefined) return;

						refs.current[`team_${team_index}_highlight`].to({
							opacity:0,
							duration:0.4,
							easing:Konva.Easings.StrongEaseInOut,
						});
					},
				});
			}

			if(refs.current['digits_tapper'] != undefined) {
				refs.current['digits_tapper'].setAttrs({
					y:0,
				});
			}

			changeRound();

			if(buttons.current == undefined) return;

			if(all_matches_answered.current === false) {
				buttons.current.goNextMatch(current_match.current);
			} else {
				buttons.current.enableFinish(team_id);
			}
			return;
		}

		if(refs.current['digits_tapper'] != undefined) {
			refs.current['digits_tapper'].setAttrs({
				y:real_used_size.h * 1.1,
			});
		}

		refs.current['teams_c'].to({
			...group_props_off,
			y:team_h * ((has_step_instructions) ? 0.85 : 0.95),
			...fast_transition_props,
		});

		if(bracket === false && has_step_instructions && refs.current['step_instructions'] != undefined) {
			refs.current['step_instructions'].to({
				opacity:0,
				y:team_h + text_padding,
				duration:default_transition_props.duration,
				easing:Konva.Easings.StrongEaseOut,
				onFinish:() => {
					if(refs.current['step_instructions'] == undefined) return;

					refs.current['step_instructions'].setAttrs({
						text:stage.guess_score_step_2_instructions,
					});
	
					refs.current['step_instructions'].to({
						opacity:1,
						y:team_h - 2.5 * text_padding,
						easing:Konva.Easings.StrongEaseOut,
						duration:fast_transition_props.duration,
					});
				},
			});
		}

		const loser_team_index = (team_index == 1) ? 2 : 1;
		refs.current[`team_${team_index}_logo`].to({
			...winner_team_props,
			...fast_transition_props,
		});

		refs.current[`team_${loser_team_index}_logo`].to({
			...loser_team_props,
			...fast_transition_props,
		});

		if(refs.current[`winner_highlight_${team_index}`] != undefined) {
			refs.current[`winner_highlight_${team_index}`].to({
				opacity:1,
				duration:0.5 + 0.5 * Math.random(),
				easing:Konva.Easings.StrongEaseInOut,
			});
		}

		if(refs.current[`winner_highlight_${loser_team_index}`] != undefined) {
			refs.current[`winner_highlight_${loser_team_index}`].to({
				opacity:0.2,
				duration:1,
				easing:Konva.Easings.StrongEaseInOut,
			});
		}

		refs.current[`team_0_score`].to({
			...group_props_on,
			y:digits_final_y,
			...fast_transition_props,
			onFinish:() => {
				if(buttons === false) return;
				buttons.current.enableNext();
			},
		});
	};

	const updateUserAnswer = () => {
		if(['margin', 'matches'].includes(type)) {
			user_bets.current[current_match.current].interval = intervals[match_results.current.team_1];
			return;
		}

		// save the digits in the matches object:
		const team_1_id = matches.current[current_match.current].id_1;
		const team_2_id = matches.current[current_match.current].id_2;

		user_bets.current[current_match.current][team_1_id] = match_results.current.team_1;
		user_bets.current[current_match.current][team_2_id] = match_results.current.team_2;
	};

	const createNextMatch = (match_index, team_pos, winner_team_id) => {
		matches.current[match_index][`id_${team_pos}`] = winner_team_id;
	};

	const onSelectWinnerFromCanvas = (team_index) => {
		selectWinner(team_index);
	};

	const displayNextMatchTeams = (match, cb) => {
		if(bracket === false && refs.current['step_instructions'] != undefined) {
			refs.current['step_instructions'].to({
				opacity:1,
				y:team_h - text_padding,
				duration:fast_transition_props.duration,
				easing:Konva.Easings.StrongEaseOut,
			});
		}

		if(ensureBoolean(matches.current[current_match.current]?.x2 ?? false)) {
			refs.current['x2'].display();
		}

		if(buttons !== false && ['margin', 'matches'].includes(type)) {
			if(match !== 'prev') {
				buttons.current.disableNext();
			} else {
				buttons.current.enableNext();
			}
		}

		if(bracket !== false && refs.current['digits_tapper'] != undefined) {
			refs.current['digits_tapper'].setAttrs({
				y:real_used_size.h * 1.1,
			});
		}

		clearTimeout(throttle_tip.current);
		throttle_tip.current = setTimeout(() => {
			showTip();
		}, (delay_to_tip * 1000));

		if(cb != undefined) cb();
		if(bracket !== false) bracket_ref.current.increaseMatch();
	};

	const endGame = () => {
		if(bracket === false) return;

		clearTimeout(throttle_tip.current);
		hideTip();

		[...Array(2).keys()].map((t) => {
			changeTeam(t, false, 0, null, true);
		});
	};

	const navigateToAnotherMatch = (match, cb, delay = 0) => {
		if(is_builder && !is_test) return;

		hideTip();
		updateUserAnswer();

		if(!['margin', 'only_winner'].includes(type)) refs.current['x2'].hide();

		let animate_to_match = true;
		// update the next match ID to be displayed:
		switch(match) {
			case 'prev':
				// go previous match (if exists):
				if(current_match.current == 0) return;
				current_match.current--;
				break;

			case 'next':
				// go next match (if exists):
				if(current_match.current == matches.current.length - 1) {
					all_matches_answered.current = true;
					endGame();
					return;
				}

				current_match.current++;
				break;

			default:
				animate_to_match = false;

				// go to a specific match (if exists) (to edit its results):
				if(match < 0 || match > matches.current.length - 1) return;
				current_match.current = match;
				break;
		}

		if(bracket === false && ['margin', 'matches'].includes(type)) {
			if(refs.current['digits_tapper'] != undefined) {
				refs.current['digits_tapper'].setAttrs({
					y:(match == 'prev') ? real_used_size.h * 1.1 : digits_final_step_1_y - text_padding,
				});
			}

			if(match != 'prev') {
				refs.current['teams_c'].to({
					...group_props_on,
					y:team_h * 1.15,
					...default_transition_props,
				});
			}

			if(is_editing_display === false && match != 'prev') {
				refs.current[`team_0_score`].to({
					...group_props_off,
					y:real_used_size.h + 50,
					...default_transition_props,
				});
			} else {
				refs.current[`team_0_score`].to({
					...group_props_on,
					y:digits_final_y,
					...default_transition_props,
				});

				if(match == 'prev') resetDigits();
			}
		}

		if(bracket === false && ['result'].includes(type) && match == 'prev') {
			resetDigits();
		}

		if(header !== false && header.current.updateCustomDescription != undefined) {
			header.current.updateCustomDescription(matches.current[current_match.current]?.description ?? "");
		}

		if(match != 'prev' && !['only_winner'].includes(type)) {
			setTimeout(() => {
				resetDigits();
			}, (fast_transition_props.duration * 1000));
		}

		if(bracket === false && refs.current['step_instructions'] != undefined) {
			refs.current['step_instructions'].to({
				opacity:0,
				y:team_h + text_padding,
				duration:default_transition_props.duration,
				easing:Konva.Easings.StrongEaseOut,
			});
		}

		if(animate_to_match) {
			[...Array(2).keys()].map((t) => {
				changeTeam(t, ((match == 'prev') ? true : false), delay, (t == 1) ? () => {
					displayNextMatchTeams(match, cb);
				} : null);
			});
		}
	};

	const redrawCanvas = () => {
		refs.current['layer'].draw();
	};

	const onMouseEnter = (team_index) => {
		document.body.style.cursor = 'pointer';
		if(bracket === false) return;

		if(tint_winner && is_touch_device === false) {
			refs.current[`team_${team_index}_highlight`].to({
				opacity:0.7,
				duration:0.3,
				easing:Konva.Easings.StrongEaseInOut,
			});
		}

		refs.current[`team_${team_index}_logo`].to({
			scaleX:1.1,
			scaleY:1.1,
			duration:0.3,
			easing:Konva.Easings.BounceEaseOut,
		});
	};

	const onMouseLeave = (team_index) => {
		document.body.style.cursor = 'default';
		if(bracket === false) return;

		if(tint_winner && is_touch_device === false) {
			refs.current[`team_${team_index}_highlight`].to({
				opacity:0,
				duration:0.4,
				easing:Konva.Easings.StrongEaseInOut,
			});
		}

		refs.current[`team_${team_index}_logo`].to({
			scaleX:1,
			scaleY:1,
			duration:0.4,
			easing:Konva.Easings.BounceEaseOut,
		});
	};

	useImperativeHandle(ref, () => ({
		navigateToMatch(match, cb) {
			navigateToAnotherMatch(match, cb);
		},

		finishGame(go_to_resume = true, cb) {
			updateUserAnswer();
			if(!is_builder) window.removeEventListener('beforeunload', preventLeaving);

			const response = {};
			matches.current.map((match, m) => {
				if(['margin', 'matches'].includes(type)) {
					response[match.id] = {
						winner:user_bets.current[m].winner,
						interval:user_bets.current[m].interval,
					};
					return;
				}

				if(['only_winner'].includes(type)) {
					response[match.id] = {
						winner:user_bets.current[m].winner,
					};
					return;
				}

				response[match.id] = {
					id_1:user_bets.current[m][match.id_1],
					id_2:user_bets.current[m][match.id_2],
				};
			});

			if(go_to_resume === false || bracket !== false) {
				props.onEnded(true, { guess_score:response });
				if(cb != undefined) cb();
				return;
			}

			props.onResume(true, response);
			if(cb != undefined) cb();
		},

		updateMatches() {
			updateUserAnswer();
			if(!is_builder) window.removeEventListener('beforeunload', preventLeaving);

			const response = {};
			matches.current.map((match, m) => {
				if(['only_winner'].includes(type)) {
					response[match.id] = {
						winner:user_bets.current[m].winner,
					};
					return;
				}

				if(['margin', 'matches'].includes(type)) {
					response[match.id] = {
						winner:user_bets.current[m].winner,
						interval:user_bets.current[m].interval,
					};
					return;
				}

				response[match.id] = {
					id_1:user_bets.current[m][match.id_1],
					id_2:user_bets.current[m][match.id_2],
				};
			});

			props.onUpdate(response);
		},
	}), []);

	if(!full_loaded) return null;

	const main_classes = ['canvas_cont'];
	if(display_outter_border === false) main_classes.push('not_bordered');

	const bg_image = indexed_images?.bg_image ?? false;
	const bg_image_scale = (bg_image !== false) ? Math.min((final_size.current.w / bg_image.image_w), ((final_size.current.h - bracket_h.current) / bg_image.image_h)) : 1;

	const block_w = digit_box_w * score_num_digits;

	let value_max_length = 0;
	intervals.map((v) => {
		value_max_length = Math.max(value_max_length, `${v}`.length);
	});

	const teams_to_play = [...Array((['margin', 'matches', 'only_winner'].includes(type)) ? 1 : 2).keys()];

	let teams_c_props = {
		...group_props_on,
	};

	const team_default_props = [{ ...winner_team_props }, { ...winner_team_props }];

	if((['margin', 'matches'].includes(type)) && is_editing_display !== false) {
		teams_c_props = {
			...group_props_off,
			y:team_h * ((has_step_instructions) ? 0.85 : 0.95),
		}

		let loser_index = 1;
		if(matches.current[current_match.current].id_2 == user_bets.current[current_match.current].winner) {
			loser_index = 0;
		}
		team_default_props[loser_index] = { ...loser_team_props };
	}

	if(has_step_instructions && bracket !== false) {
		teams_c_props.y = 1.5 * text_padding;
	}

	return (
		<Stage
			ref={stage_ref}
			className={main_classes.join(" ")}
			width={size.current.w}
			height={size.current.h}
		>
			<Layer
				ref={(node) => { refs.current['layer'] = node; }}
				scaleX={size.current.w / final_size.current.w}
				scaleY={size.current.h / final_size.current.h}
			>
				<Group
					ref={(node) => { refs.current['bg'] = node; }}
					preventDefault={prevent_default}
				>
					<Rect
						width={final_size.current.w}
						height={final_size.current.h}
						fill={bg_color}
						preventDefault={prevent_default}
					/>

					{(bg_image !== false) && (
						<Rect
							y={bracket_h.current}
							width={final_size.current.w}
							height={final_size.current.h - bracket_h.current}
							fillPatternImage={bg_image.fillPatternImage}
							fillPatternScaleX={bg_image_scale}
							fillPatternScaleY={bg_image_scale}
							preventDefault={prevent_default}
						/>
					)}
				</Group>

				{(bracket !== false) && (
					<Group
						ref={(node) => { refs.current['bracket_c'] = node; }}
						preventDefault={prevent_default}
					>
						{React.cloneElement(bracket, {
							...props,
							ref:bracket_ref,
							in_game:true,
							forced_w:real_used_size.w,
							forced_h:bracket_h.current,
							createNextMatch,
							redrawCanvas,
							onSelectWinnerFromCanvas,
						})}
					</Group>
				)}

				{/* Hidden instructions message */}
				{(has_step_instructions && bracket) && (
					<Group
						x={real_used_size.w / 4}
						y={bracket_h.current - tip_text_h}
						preventDefault={prevent_default}
					>
						<Group
							clipFunc={(ctx) => {
								ctx.rect(0, 0, real_used_size.w / 2, tip_text_h);
							}}
							preventDefault={prevent_default}
						>
							<Group
								ref={(node) => { refs.current['tip_instructions'] = node; }}
								y={(!is_builder || is_test) ? tip_text_h : 0}
								preventDefault={prevent_default}
							>
								<Rect
									width={real_used_size.w / 2}
									height={tip_text_h}
									fill={bg_color}
									preventDefault={prevent_default}
								/>

								<Text
									{...default_text_props}
									fontSize={fine_tune_scale * 24}
									width={real_used_size.w / 2}
									height={tip_text_h}
									opacity={1}
									//opacity={(is_builder && !is_test) ? 1 : 0}
									text={stage.guess_score_step_2_instructions}
									align='center'
									verticalAlign='middle'
									preventDefault={prevent_default}
								/>
							</Group>
						</Group>

						<Group
							x={real_used_size.w / 4}
							y={tip_text_h - text_padding / 2}
							preventDefault={prevent_default}
						>
							{[...Array(total_tip_arrows).keys()].map((arrow) => {
								const arrow_key = `effect_tip_${arrow + 1}`;

								return (
									<Line
										ref={(node) => { refs.current[arrow_key] = node; }}
										points={[10,0, 0,10, -10,0]}
										scaleX={1}
										scaleY={1}
										strokeWidth={1.5}
										stroke={`rgba(${text_color.join(", ")}, 1)`}
										lineCap='round'
										lineJoin='round'
										fill='transparent'
										opacity={0}
										preventDefault={prevent_default}
									/>
								)
							})}
						</Group>
					</Group>
				)}

				<Group
					y={bracket_h.current}
					clipFunc={(ctx) => {
						ctx.rect(0, 0, real_used_size.w, real_used_size.h);
					}}
					preventDefault={prevent_default}
				>
					{['margin', 'matches'].includes(type) && tint_winner && [...Array(2).keys()].map((t) => {
						const team_index = t + 1;
						const team = getTeamInfo(team_index);

						if(!matches.current[current_match.current]) return null;

						const img_key = `logo_${matches.current[current_match.current][`id_${team_index}`]}`;
						const img = (isNotEmpty(indexed_images, img_key)) ? indexed_images[img_key] : false;

						const team_custom_color = (isNotEmpty(team, 'color')) ? colorToArray(team.color) : false;
						const average_team_color = `${img?.avg_color?.r ?? 0}, ${img?.avg_color?.g ?? 0}, ${img?.avg_color?.b ?? 0}`;
						const team_color = (team_custom_color !== false) ? `${team_custom_color[0]}, ${team_custom_color[1]}, ${team_custom_color[2]}` : average_team_color;

						return (
							<Rect
								ref={(node) => { refs.current[`winner_highlight_${team_index}`] = node; }}
								width={real_used_size.w}
								height={real_used_size.h}
								offsetX={real_used_size.w / 2}
								x={real_used_size.w / 2}
								scaleX={-1 * (2 * t - 1)}
								opacity={1}
								fillRadialGradientEndRadius={360}
								fillRadialGradientStartPoint={{ x:0, y:team_h / 4 }}
								fillRadialGradientEndPoint={{ x:team_w, y:team_h / 2 }}
								fillRadialGradientColorStops={[ 0, `rgba(${team_color}, 0.5)`, 1, `rgba(${team_color}, 0)` ]}
								preventDefault={prevent_default}
							/>
						)
					})}

					<X2
						ref={(node) => { refs.current['x2'] = node; }}
						bg_color={x2_color}
						text_color={x2_text_color}
						w={real_used_size.w}
						h={final_size.current.h - bracket_h.current}
						padding={text_padding}
						preventDefault={prevent_default}
					/>

					{(bracket !== false && current_round.current <= num_rounds) && (
						<Group>
							<Rect
								ref={(node) => { refs.current['round_change_bg'] = node; }}
								width={real_used_size.w}
								height={(final_size.current.h - bracket_h.current)}
								fillRadialGradientEndRadius={360}
								fillRadialGradientStartPoint={{ x:real_used_size.w / 2, y:(final_size.current.h - bracket_h.current) / 2 }}
								fillRadialGradientEndPoint={{ x:real_used_size.w / 2, y:1.2 * (final_size.current.h - bracket_h.current) }}
								fillRadialGradientColorStops={[ 0, `rgba(0, 0, 0, 0)`, 1, `rgba(0, 0, 0, 0.5)` ]}
								opacity={0}
								preventDefault={prevent_default}
							/>

							<Text
								ref={(node) => { refs.current['round_change_text'] = node; }}
								{...default_text_props}
								fontSize={100}
								x={real_used_size.w + 100}
								height={final_size.current.h - bracket_h.current}
								text={`${current_round.current} - ${rounds[current_round.current - 1].title}`}
								align='center'
								verticalAlign='middle'
								preventDefault={prevent_default}
							/>
						</Group>
					)}

					{(has_step_instructions && bracket === false) && (
						<Text
							ref={(node) => { refs.current['step_instructions'] = node; }}
							{...default_text_props}
							fontSize={25}
							x={real_used_size.w * 0.05}
							y={team_h + text_padding}
							width={real_used_size.w * 0.9}
							height={text_h}
							opacity={0}
							text={stage.guess_score_step_1_instructions}
							align='center'
							verticalAlign='middle'
							preventDefault={prevent_default}
						/>
					)}

					<Group
						ref={(node) => { refs.current['teams_c'] = node; }}
						{...teams_group_props_default}
						{...teams_c_props}
						preventDefault={prevent_default}
					>
						{[...Array(2).keys()].map((t) => {
							const team_index = t + 1;
							const team = getTeamInfo(team_index);

							if(!matches.current[current_match.current] || !team) return null;

							const img_key = `logo_${matches.current[current_match.current][`id_${team_index}`]}`;
							const img = (isNotEmpty(indexed_images, img_key)) ? indexed_images[img_key] : false;

							const team_custom_color = (isNotEmpty(team, 'color')) ? colorToArray(team.color) : false;
							const average_team_color = `${img?.avg_color?.r ?? 0}, ${img?.avg_color?.g ?? 0}, ${img?.avg_color?.b ?? 0}`;
							const team_color = (team_custom_color !== false) ? `${team_custom_color[0]}, ${team_custom_color[1]}, ${team_custom_color[2]}` : average_team_color;

							const image_scale = (img !== false) ? Math.min((logo_size / img.image_w), (logo_size / img.image_h)) : 1;

							return (
								<Group
									key={`team_${t}`}
									ref={(node) => { refs.current[`team_${team_index}_c`] = node; }}
									x={t * team_w}
									width={team_w}
									height={team_h}
									team_id={team.id}
									onClick={() => selectWinner(team_index)}
									onTap={() => selectWinner(team_index)}
									preventDefault={prevent_default}
									onMouseEnter={() => onMouseEnter(team_index)}
									onMouseLeave={() => onMouseLeave(team_index)}
								>
									<Rect
										ref={(node) => { refs.current[`team_${team_index}_highlight`] = node; }}
										width={team_w}
										height={team_h}
										x={team_w / 2}
										y={-27}
										preventDefault={prevent_default}
										offsetX={team_w / 2}
										scaleX={(team_index > 1) ? -1 : 1}
										fillRadialGradientEndRadius={360}
										fillRadialGradientStartPoint={{ x:0, y:team_h }}
										fillRadialGradientEndPoint={{ x:team_w / 2, y:team_h / 4 }}
										fillRadialGradientColorStops={[ 0, `rgba(${team_color}, 0.5)`, 0.6, `rgba(${team_color}, 0)` ]}
										opacity={0}
									/>

									<Rect
										ref={(node) => { refs.current[`team_${team_index}_logo`] = node; }}
										{...getLogoProps(t)}
										{...team_default_props[t]}
										orig_y={logo_y + logo_size}
										y={logo_y + logo_size}
										width={logo_size}
										height={logo_size}
										offsetX={logo_size / 2}
										offsetY={logo_size}
										fillPatternImage={img.fillPatternImage}
										fillPatternScaleX={image_scale}
										fillPatternScaleY={image_scale}
										fill={team?.color ?? `rgba(${text_color.join(", ")}, 1)`}
										fillPriority={(img !== false) ? 'pattern' : 'fill'}
										preventDefault={prevent_default}
									/>

									<Text
										ref={(node) => { refs.current[`team_${t + 1}_name`] = node; }}
										{...getNameProps(t)}
										{...default_text_props}
										lineHeight={1.1}
										width={text_w}
										height={text_h + 2 * text_padding}
										y={text_y}
										align='center'
										verticalAlign='middle'
										text={getTeamName(team)}
										ellipsis={true}
										preventDefault={prevent_default}
									/>
								</Group>
							)
						})}
					</Group>

					{!['only_winner'].includes(type) && teams_to_play.map((t) => {
						const block_center_x = real_used_size.w / (teams_to_play.length * 2) + t * real_used_size.w / 2;

						const min_block_w = digit_box_w * Math.max(((['margin', 'matches'].includes(type)) ? value_max_length : score_num_digits), 2);

						let default_props = { ...group_props_off };
						let block_y = digits_final_step_1_y;
						if(['margin', 'matches'].includes(type)) {
							if(is_editing_display === false) {
								block_y = real_used_size.h + 50;
							} else {
								block_y = digits_final_y;
								default_props = { ...group_props_on };
							}
						}

						return (
							<Group
								key={`team_${t}_score`}
								ref={(node) => { refs.current[`team_${t}_score`] = node; }}
								{...digits_group_props_default}
								{...default_props}
								y={block_y}
								preventDefault={prevent_default}
							>
								<Line
									x={block_center_x - min_block_w / 2 - 5}
									{...digits_separator_props}
									scaleX={-1}
									preventDefault={prevent_default}
								/>

								<Group
									x={block_center_x - block_w / 2}
								>
									{[...Array(score_num_digits).keys()].map((i) => {
										return (
											<DigitPicker
												key={`digit_${t}_${i}`}
												ref={(node) => { refs.current[`digit_${t}_${i}`] = node; }}
												text_color={text_color}
												main_color={main_color}
												team={t + 1}
												type={type}
												values={intervals}
												value_max_length={value_max_length}
												position={score_num_digits - i - 1}
												digits_direction={digits_direction}
												onChange={changeDigit}
												item_props={{
													x:digit_box_w * i,
												}}
											/>
										)
									})}
								</Group>

								<Line
									x={block_center_x + (min_block_w + separator_w) / 2 - 5}
									{...digits_separator_props}
									preventDefault={prevent_default}
								/>
							</Group>
						)
					})}

					{(type == 'result') && (
						<Line
							points={[ -separator_line_w / 2, 0, separator_line_w / 2, 0 ]}
							strokeWidth={6}
							stroke={`rgba(${text_color.join(", ")}, 1)`}
							x={300}
							y={350 + separator_h / 2 + text_padding / 2}
							preventDefault={prevent_default}
						/>
					)}

					{(bracket !== false || (['margin', 'matches'].includes(type)) && is_editing_display === false) && (
						<Rect
							ref={(node) => { refs.current['digits_tapper'] = node; }}
							width={real_used_size.w}
							height={real_used_size.h / 2}
							y={(bracket !== false) ? 0 : digits_final_step_1_y - text_padding}
							fill='orange'
							opacity={0}
							preventDefault={prevent_default}
						/>
					)}
				</Group>
			</Layer>
		</Stage>
	)
})

export default GuessScoreCanvas;